import React, { ClipboardEvent, KeyboardEvent, useRef } from "react";

import { cn } from "../../../../../lib/utils";

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value,
  onChange,
  disabled = false,
  error = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = Array.from({ length }, (_, index) => value[index] ?? "");

  const focus = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const updateDigit = (index: number, digit: string) => {
    const next = [...digits];
    next[index] = digit;

    onChange(next.join("").slice(0, length));
  };

  const handleChange = (index: number, input: string) => {
    const digit = input.replace(/\D/g, "").slice(-1);

    if (!digit && input) return;

    updateDigit(index, digit);

    if (digit && index < length - 1) {
      focus(index + 1);
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "Backspace":
        if (digits[index]) {
          updateDigit(index, "");
          return;
        }

        if (index > 0) {
          updateDigit(index - 1, "");
          focus(index - 1);
        }
        break;

      case "ArrowLeft":
        if (index > 0) {
          focus(index - 1);
        }
        break;

      case "ArrowRight":
        if (index < length - 1) {
          focus(index + 1);
        }
        break;
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const otp = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);

    if (!otp) return;

    onChange(otp);
    focus(Math.min(otp.length, length) - 1);
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`Digit ${index + 1}`}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className={cn(
            "h-10 w-10 rounded-lg border bg-[#FAFAFA] text-center",
            "text-lg font-bold font-nunito outline-none",
            "transition-all duration-200",
            "sm:h-12 sm:w-12 sm:text-xl",
            "md:h-[60px] md:w-[60px] md:text-2xl",
            "dark:bg-gray-800 dark:text-white",
            "focus:border-[#00C49A] focus:ring-2 focus:ring-[#00C49A]/20",
            error ? "border-[#EC5454]" : "border-[#DCDCDC] dark:border-gray-600",
            disabled && "cursor-not-allowed opacity-50",
          )}
        />
      ))}
    </div>
  );
};

export default OTPInput;
